import { clsx } from 'clsx'
import { useLayoutVariant } from '../../hooks/useLayoutVariant'

export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  ...props
}) {
  const { isMobileUI, isAdminAppUI } = useLayoutVariant()
  
  const baseClasses =
    'inline-flex items-center justify-center font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none'
  
  const baseRounded = isAdminAppUI ? 'rounded-[10px]' : isMobileUI ? 'rounded-2xl' : 'rounded-lg'
  const baseActive = isAdminAppUI ? '' : 'active:scale-95'
  
  const variantClasses = {
    primary: isAdminAppUI
      ? 'bg-[#00c896] text-[#0f1623] border border-transparent hover:bg-[#00e0a8] focus:ring-[#00c896]/40'
      : isMobileUI
      ? 'bg-gradient-to-r from-pastel-teal to-primary-400 text-white hover:opacity-95 focus:ring-pastel-teal shadow-soft'
      : 'bg-gradient-to-r from-pastel-teal to-primary-400 text-white hover:from-primary-600 hover:to-primary-500 focus:ring-pastel-teal shadow-soft hover:shadow-md hover:scale-[1.02] disabled:hover:scale-100',
    secondary: isAdminAppUI
      ? 'bg-[#1a2535] text-[#e8edf5] border border-[#243246] hover:border-[#00c896] hover:text-[#00c896] focus:ring-[#00c896]/40'
      : isMobileUI
      ? 'bg-white text-pastel-ink rounded-2xl font-medium hover:bg-pastel-cream focus:ring-pastel-lavender border border-pastel-pink'
      : 'bg-white dark:bg-slate-600 text-pastel-ink dark:text-slate-100 hover:bg-pastel-cream dark:hover:bg-slate-500 focus:ring-pastel-lavender border border-pastel-pink dark:border-slate-500 hover:shadow-md hover:scale-[1.02] disabled:hover:scale-100',
    danger: isAdminAppUI
      ? 'bg-[rgba(239,83,80,0.12)] border border-[rgba(239,83,80,0.24)] text-[#ef5350] hover:bg-[rgba(239,83,80,0.18)] focus:ring-[#ef5350]/30'
      : 'bg-rose-500 hover:bg-rose-600 dark:bg-rose-500 dark:hover:bg-rose-600 text-white focus:ring-rose-400 shadow-soft hover:shadow-md hover:scale-[1.02] disabled:hover:scale-100',
    success: isAdminAppUI
      ? 'bg-[rgba(102,187,106,0.12)] border border-[rgba(102,187,106,0.24)] text-[#66bb6a] hover:bg-[rgba(102,187,106,0.18)] focus:ring-[#66bb6a]/30'
      : 'bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-500 text-white focus:ring-emerald-400 shadow-soft hover:shadow-md hover:scale-[1.02] disabled:hover:scale-100',
    outline: isAdminAppUI
      ? 'border border-[#243246] text-[#e8edf5] bg-[#1a2535] hover:border-[#00c896] hover:text-[#00c896] focus:ring-[#00c896]/30'
      : 'border-2 border-pastel-pink/60 dark:border-slate-500 text-pastel-ink dark:text-slate-300 bg-white dark:bg-slate-600 hover:bg-pastel-cream dark:hover:bg-slate-500 focus:ring-pastel-lavender hover:shadow-md hover:scale-[1.02] disabled:hover:scale-100',
    ghost: isAdminAppUI
      ? 'text-[#e8edf5] hover:bg-[rgba(0,200,150,0.06)] hover:text-[#00c896] focus:ring-[#00c896]/30'
      : 'text-pastel-ink dark:text-slate-300 hover:bg-pastel-cream dark:hover:bg-slate-600 focus:ring-pastel-lavender hover:scale-[1.02] disabled:hover:scale-100',
  }
  
  const sizeClasses = {
    sm: 'px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs',
    md: 'px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm',
    lg: 'px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base',
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={clsx(
        baseClasses,
        baseRounded,
        baseActive,
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      {...props}
    >
      {loading ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Cargando...
        </>
      ) : (
        children
      )}
    </button>
  )
}
